import JsBarcode from 'jsbarcode';
import { formatCurrency } from './currency';
import { priceWithVat } from './sale-vat';
import {
  StickerDetailPart,
  StickerLayout,
  StickerPaperPreset,
  StickerPaperPresetKey,
  StickerPosition,
  cropMarksForSheet,
  cutLinesForSheet,
  defaultStickerPaperPresetKey,
  fitStickerContent,
  layoutStickerPositions,
  stickerDetailSeparator,
  stickerPaperPresets,
} from './sticker-paper';

// Barcode sticker (Stitches S8, flag product.barcodeSticker): Code 128 of the SKU, on a label roll
// or a sheet of labels (utilities/sticker-paper.ts). Frontend only; the delivery sticker is
// untouched.

export type BarcodePayload =
  | { isBarcodable: true; format: 'CODE128'; value: string }
  | { isBarcodable: false; message: string };

// Printable ASCII, which Code 128 encodes and scanners read back as typed.
const isCode128Character = (character: string) => {
  const code = character.charCodeAt(0);
  return code >= 32 && code <= 126;
};

// The SKU exactly as stored. A SKU Code 128 can't hold is refused, never altered.
export const barcodePayloadForSku = (sku: string): BarcodePayload => {
  if (!sku) {
    return { isBarcodable: false, message: "SKU can't be barcoded: the product has no SKU." };
  }
  const unsupportedCharacters = Array.from(new Set(Array.from(sku))).filter(
    (character) => !isCode128Character(character),
  );
  if (unsupportedCharacters.length) {
    return {
      isBarcodable: false,
      message: `SKU can't be barcoded: "${sku}" has characters Code 128 cannot hold (${unsupportedCharacters.join(
        ' ',
      )}). Use English letters, digits and symbols.`,
    };
  }
  return { isBarcodable: true, format: 'CODE128', value: sku };
};

export const barcodeModuleWidth = 1.4;
export const barcodeQuietZoneWidth = 10 * barcodeModuleWidth;

export const renderBarcodeSvgMarkup = (value: string): string => {
  const svgElement = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  JsBarcode(svgElement, value, {
    format: 'CODE128',
    height: 30,
    width: barcodeModuleWidth,
    // Scanners need a blank quiet zone of at least 10 bar widths at each end, and the gap at the
    // top keeps the bars off the product name above them.
    marginLeft: barcodeQuietZoneWidth,
    marginRight: barcodeQuietZoneWidth,
    marginTop: 4,
    marginBottom: 0,
    fontSize: 11,
    displayValue: true,
  });
  return svgElement.outerHTML;
};

export const escapeHtml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export type StickerLabelContent = {
  productName: string;
  sku: string;
  priceText: string;
  barcodeSvgMarkup: string;
  // Company, showroom and print date the user chose, before fitting.
  detailTexts: Partial<Record<StickerDetailPart, string>>;
};

const millimetres = (value: number) => `${Number(value.toFixed(2))}mm`;

const labelMarkup = (
  preset: StickerPaperPreset,
  position: StickerPosition,
  content: StickerLabelContent,
  layout: StickerLayout,
) => {
  const detailLine = layout.detailParts
    .map((part) => content.detailTexts[part] ?? '')
    .join(stickerDetailSeparator);
  const bleedBox =
    preset.bleed > 0
      ? `<div class="bleed" style="left:${millimetres(position.left - preset.bleed)};top:${millimetres(
          position.top - preset.bleed,
        )};width:${millimetres(preset.labelWidth + 2 * preset.bleed)};height:${millimetres(
          preset.labelHeight + 2 * preset.bleed,
        )}"></div>`
      : '';
  return `${bleedBox}<div class="label" style="left:${millimetres(position.left)};top:${millimetres(
    position.top,
  )};width:${millimetres(preset.labelWidth)};height:${millimetres(preset.labelHeight)}">
        <div class="safe" style="inset:${millimetres(preset.safeArea)}">
          <div class="name" style="font-size:${layout.nameFontSize}pt">${escapeHtml(
            content.productName,
          )}</div>
          ${
            detailLine
              ? `<div class="detail" style="font-size:${layout.detailFontSize}pt">${escapeHtml(
                  detailLine,
                )}</div>`
              : ''
          }
          <div class="barcode" style="height:${millimetres(layout.barcodeHeight)}">${
            content.barcodeSvgMarkup
          }</div>
          <div class="price" style="font-size:${layout.priceFontSize}pt">${escapeHtml(
            content.priceText,
          )}</div>
        </div>
      </div>`;
};

// On sheets, the positions this job leaves empty (before the start position, after the last copy)
// are outlined on screen so the preview shows where each sticker lands. They never print.
const unusedSlotMarkup = (preset: StickerPaperPreset, pagePositions: StickerPosition[]): string => {
  if (preset.kind === 'roll') return '';
  const usedCells = new Set(pagePositions.map((position) => `${position.row}:${position.column}`));
  const outlines: string[] = [];
  for (let row = 0; row < preset.rows; row += 1) {
    for (let column = 0; column < preset.columns; column += 1) {
      if (usedCells.has(`${row}:${column}`)) continue;
      outlines.push(
        `<div class="unused-slot" style="left:${millimetres(
          preset.marginLeft + column * preset.pitchHorizontal,
        )};top:${millimetres(preset.marginTop + row * preset.pitchVertical)};width:${millimetres(
          preset.labelWidth,
        )};height:${millimetres(preset.labelHeight)}"></div>`,
      );
    }
  }
  return outlines.join('');
};

// Sheet-level cut guides on the cut sheet: crop marks always, cut lines when asked for. Both print.
const cutGuideMarkup = (preset: StickerPaperPreset, printCutLines: boolean): string => {
  const box = (
    className: string,
    mark: { left: number; top: number; width: number; height: number },
  ) =>
    `<div class="${className}" style="left:${millimetres(mark.left)};top:${millimetres(
      mark.top,
    )};width:${millimetres(mark.width)};height:${millimetres(mark.height)}"></div>`;
  return [
    ...cropMarksForSheet(preset).map((mark) => box('crop-mark', mark)),
    ...(printCutLines ? cutLinesForSheet(preset).map((line) => box('cut-line', line)) : []),
  ].join('');
};

// The whole print job: every copy at its millimetre position, page after page. Guides (trim, safe
// area) show on screen only; on the cut sheet the crop marks and, when chosen, the cut lines print.
// Print at 100% / actual size: any fit-to-page scaling moves every label off its position.
export const buildStickerSheetHtml = ({
  preset,
  content,
  copies,
  startPosition,
  printCutLines = true,
}: {
  preset: StickerPaperPreset;
  content: StickerLabelContent;
  copies: number;
  startPosition: number;
  printCutLines?: boolean;
}): string => {
  const layout = fitStickerContent({
    labelWidth: preset.labelWidth,
    labelHeight: preset.labelHeight,
    safeArea: preset.safeArea,
    detailTexts: content.detailTexts,
  });
  const positions = layoutStickerPositions({ preset, copies, startPosition });
  const pageCount = positions.length ? positions[positions.length - 1].page + 1 : 1;
  const pages = Array.from({ length: pageCount }, (_, page) => {
    const pagePositions = positions.filter((position) => position.page === page);
    return (
      unusedSlotMarkup(preset, pagePositions) +
      pagePositions.map((position) => labelMarkup(preset, position, content, layout)).join('') +
      cutGuideMarkup(preset, printCutLines)
    );
  });
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(content.sku)}</title>
    <style>
      @page { size: ${millimetres(preset.pageWidth)} ${millimetres(preset.pageHeight)}; margin: 0; }
      body { margin: 0; font-family: Arial, sans-serif; }
      .page { position: relative; width: ${millimetres(preset.pageWidth)}; height: ${millimetres(
        preset.pageHeight,
      )}; overflow: hidden; page-break-after: always; break-after: page; background: #fff; }
      .page:last-child { page-break-after: auto; break-after: auto; }
      .label { position: absolute; box-sizing: border-box; outline: 0.2mm dashed #bfbfbf; }
      .bleed { position: absolute; background: rgba(250, 173, 20, 0.15); }
      .safe { position: absolute; display: flex; flex-direction: column; align-items: center; justify-content: space-between; outline: 0.15mm dotted #91caff; overflow: hidden; }
      .name, .detail, .price { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; line-height: 1.15; }
      .name { font-weight: bold; }
      .detail { color: #333; }
      .barcode { display: flex; justify-content: center; max-width: 100%; }
      .barcode svg { height: 100%; width: auto; max-width: 100%; }
      .crop-mark { position: absolute; background: #000; }
      .cut-line { position: absolute; background: #9e9e9e; }
      .unused-slot { position: absolute; box-sizing: border-box; outline: 0.2mm dashed #e5e5e5; }
      @media print {
        .label, .safe { outline: none; }
        .bleed { background: none; }
        .unused-slot { display: none; }
      }
    </style>
  </head>
  <body>
    ${pages.map((labels) => `<div class="page">${labels}</div>`).join('\n    ')}
  </body>
</html>`;
};

// The small line under the name: whichever of company, showroom and print date are chosen.
export const buildStickerDetailTexts = ({
  includeCompanyName,
  companyName,
  includeShowroomName,
  showroomName,
  includePrintDate,
  printDate,
}: {
  includeCompanyName: boolean;
  companyName?: string | null;
  includeShowroomName: boolean;
  showroomName?: string | null;
  includePrintDate: boolean;
  printDate: Date;
}): Partial<Record<StickerDetailPart, string>> => ({
  ...(includeCompanyName && companyName ? { company: companyName } : {}),
  ...(includeShowroomName && showroomName ? { showroom: showroomName } : {}),
  ...(includePrintDate ? { date: printDate.toLocaleDateString('en-GB') } : {}),
});

// ---- Choices remembered per browser ----

export type BarcodeStickerOptions = {
  paperPresetKey: StickerPaperPresetKey;
  includeVat: boolean;
  includeCompanyName: boolean;
  includeShowroomName: boolean;
  showroomWarehouseId: string | null;
  includePrintDate: boolean;
  // Cut sheet only: hairlines along every cut line.
  printCutLines: boolean;
};

export const defaultBarcodeStickerOptions: BarcodeStickerOptions = {
  paperPresetKey: defaultStickerPaperPresetKey,
  includeVat: true,
  includeCompanyName: false,
  includeShowroomName: false,
  showroomWarehouseId: null,
  includePrintDate: false,
  printCutLines: true,
};

export const barcodeStickerOptionsStorageKey = 'barcodeStickerOptions';

type OptionsStorage = Pick<Storage, 'getItem' | 'setItem'>;

// A private window or blocked site data can make storage throw or come back empty; the sticker
// then simply starts from the defaults.
export const readBarcodeStickerOptions = (
  storage: OptionsStorage | null | undefined,
): BarcodeStickerOptions => {
  try {
    const stored = JSON.parse(storage?.getItem(barcodeStickerOptionsStorageKey) ?? 'null');
    if (!stored || typeof stored !== 'object') return defaultBarcodeStickerOptions;
    const options = { ...defaultBarcodeStickerOptions, ...stored } as BarcodeStickerOptions;
    return stickerPaperPresets[options.paperPresetKey]
      ? options
      : { ...options, paperPresetKey: defaultStickerPaperPresetKey };
  } catch {
    return defaultBarcodeStickerOptions;
  }
};

export const writeBarcodeStickerOptions = (
  storage: OptionsStorage | null | undefined,
  options: BarcodeStickerOptions,
): void => {
  try {
    storage?.setItem(barcodeStickerOptionsStorageKey, JSON.stringify(options));
  } catch {
    // Not remembered this time; the sticker still prints.
  }
};

// The price line on the sticker. With VAT set and the option ticked it prints the price a customer
// pays, the same figure as the product list's "With VAT"; otherwise the base selling price.
export const barcodeStickerPriceText = ({
  sellingPrice,
  vatRatePercent,
  includeVat,
}: {
  sellingPrice: number;
  vatRatePercent: number;
  includeVat: boolean;
}): string =>
  includeVat && vatRatePercent > 0
    ? `TK ${formatCurrency(priceWithVat(sellingPrice, vatRatePercent))} incl. VAT`
    : `TK ${formatCurrency(sellingPrice)}`;
